import { getCoreUtils, kCore } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppReactRules } from "./AppReactRules";
import { AppMsgActionRules } from "./AppMsgActions";
import { AppTranslate } from "./AppTranslate";
import { AppUpdateEvery } from "./AppUpdateEvery";
import { WhatsAppAddress } from "../../Shared/WhatsAppAddress";

// AppOutbox - what the app writes when the owner presses Send.
//
// ==================================================================================================
// !! THIS FILE IS THE "UI-ORIGINATED HUMAN APPROVAL" IN DECISION 9
// ==================================================================================================
//
// The locked invariant is that no code path may send a WhatsApp message without one. The bridge
// enforces it (`Bridge/src/Outbox.ts` is the only door), and this is the only thing in the product
// that produces a record it will admit. Nothing here runs on a timer, nothing retries by itself, and
// there is no method that sends more than the one message in front of the owner.
//
// ⚠ THIS FILE IS A CONTRACT WITH `Bridge/src/Outbox.ts` AND NOTHING ENFORCES IT. The two live in
// different targets - that one is Node, this is the browser - so a field renamed on one side
// compiles perfectly on the other and produces a send that is refused with a puzzling reason. The
// SCHEMA NUMBER is the guard, exactly as `APP_INDEX_SCHEMA` is for `index.json`. Bump both together
// or not at all.
//
// ⚠ THE CHECKS HERE ARE A COURTESY, NOT THE ENFORCEMENT. They exist so the owner is told "that is
// not a chat this build can send to" while the message is still in the box, rather than twenty
// seconds later in a receipt. The bridge re-checks everything and trusts none of it - a browser is
// not a place to enforce a rule that matters.

/** Must equal `Outbox.Schema` on the bridge side. */
export const APP_OUTBOX_SCHEMA: number = 1;

export class AppSendRequest {
    public Attachment: { name: string; mime: string; data: string; voice: boolean } | null = null;
    public Id: string = "";
    public ChatId: string = "";
    public ChatTitle: string = "";
    public Text: string = "";
    public RequestedIso: string = "";
    public ApprovedBy: string = "";
    /**
     * A REACTION (2026-09-25): `Text` is "", and these name the message (`data-mid`) and the emoji ("" =
     * take it off). ⛔ NEVER A KEY OR A JID - `/send` finds those in the subscriber's own records.
     */
    public IsReaction: boolean = false;
    public ReactTo: string = "";
    public Emoji: string = "";
    /**
     * ⭐ TRANSLATION (2026-09-27): the chip is on for this chat - `Text` stays what the owner TYPED and the service
     * translates it before sending (`AppTranslate.SendField`). ⛔ Never on a reaction.
     */
    public Translate: boolean = false;
    /**
     * ⭐ A REPLY / EDIT / DELETE (2026-10-14): the kind and the target's `data-mid`. ⛔ NEVER A KEY - `/send` finds it
     * (`ReactTarget.FindAction`) and writes the file as schema 4.
     */
    public ActKind: string = "";
    public ActOn: string = "";
    /** A forward only: the 1:1 chat the message is in (the open chat); `ChatId` is the destination. */
    public ActFrom: string = "";
}

export class AppOutbox {
    public static readonly AttachmentSchema: number = 3;
    public static readonly MaxAttachmentBytes: number = 8 * 1024 * 1024;

    /** One human-approved attachment, transported in the durable request rather than a caller path. */
    public static BuildAttachment = (ChatId: string, Title: string, Caption: string, Who: string,
                                     Attachment: { name: string; mime: string; data: string; voice: boolean },
                                     Why: string[]): AppSendRequest | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            if (AppOutbox.IsJid(ChatId) === false || WhatsAppAddress.IsGroup(ChatId) === true) { Why.push("This chat cannot receive attachments."); return null; }
            if (!Who) { Why.push("Sign in before sending an attachment."); return null; }
            if (Caption.length > AppOutbox.MaxText) { Why.push("The caption is too long."); return null; }
            if (Attachment.voice && Caption.length > 0) { Why.push("Clear the message box before sending a voice note."); return null; }
            const Out: AppSendRequest = AppOutbox.Made(ChatId, Title, Caption, Who);
            Out.Attachment = Attachment;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOutbox.BuildAttachment");
            return null;
        }
    };
    /** Mirrors `Outbox.MaxText`. A longer message is a paste accident. */
    public static readonly MaxText: number = 4096;

    /**
     * Build a request, or return null with the reason in `Why`.
     *
     * ⚠ `ApprovedBy` IS NEVER DEFAULTED TO SOMETHING CONVENIENT. The bridge refuses an empty one,
     * and that refusal is the mechanism - a value invented here to get past it would be this file
     * quietly voting on its own behalf.
     */
    public static Build = (ChatId: string, Title: string, Text: string, Who: string,
                           Why: string[]): AppSendRequest | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (AppOutbox.IsJid(ChatId) === false) {
                Why.push("WACommander does not know this chat's WhatsApp number yet, so nothing can be sent "
                    + "to it. Try again after the next update (" + AppUpdateEvery.Within + ").");
                return null;
            }
            if (Text.trim().length === 0) { Why.push("Type something first."); return null; }
            if (Text.length > AppOutbox.MaxText) {
                Why.push("That is " + String(Text.length) + " characters. The limit is "
                    + String(AppOutbox.MaxText) + ".");
                return null;
            }
            if (Who.length === 0) { Why.push("Not signed in - nothing can approve a send."); return null; }
            return AppOutbox.Made(ChatId, Title, Text, Who);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOutbox.Build");
            return null;
        }
    };

    /**
     * A reaction request, or null with the reason in `Why`. The same courtesy checks as `Build`; the
     * service re-checks all of it and finds the message's key itself.
     */
    public static BuildReaction = (ChatId: string, Title: string, MessageId: string, Emoji: string,
                                   Who: string, Why: string[]): AppSendRequest | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (AppOutbox.IsJid(ChatId) === false) {
                Why.push("WACommander does not know this chat's WhatsApp number yet, so nothing can be sent to it.");
                return null;
            }
            if (AppReactRules.IsMessageId(MessageId) === false) { Why.push("That message cannot be reacted to."); return null; }
            if (Emoji.length > 0 && AppReactRules.Allowed(Emoji) === false) { Why.push("That is not a reaction WACommander sends."); return null; }
            if (Who.length === 0) { Why.push("Not signed in - nothing can approve a send."); return null; }
            const Out: AppSendRequest = AppOutbox.Made(ChatId, Title, "", Who);
            Out.IsReaction = true;
            Out.ReactTo = MessageId;
            Out.Emoji = Emoji;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOutbox.BuildReaction");
            return null;
        }
    };

    /**
     * A reply / edit / delete request, or null with the reason in `Why`. Courtesy checks only; the service re-checks
     * all of it (own message, WhatsApp's windows) and finds the key itself.
     */
    public static BuildAction = (ChatId: string, Title: string, Kind: string, MessageId: string, Text: string,
                                 Who: string, Why: string[], From: string = ""): AppSendRequest | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (AppOutbox.IsJid(ChatId) === false) { Why.push("WACommander does not know this chat's WhatsApp number yet, so nothing can be sent to it."); return null; }
            if (Kind === "forward" && (AppOutbox.IsJid(From) === false || WhatsAppAddress.IsGroup(From) === true || WhatsAppAddress.IsGroup(ChatId) === true)) {
                Why.push("A message can only be forwarded between 1:1 chats."); return null;
            }
            if (AppMsgActionRules.Kinds.indexOf(Kind) < 0 || AppReactRules.IsMessageId(MessageId) === false) { Why.push("That message cannot be answered from here."); return null; }
            if ((Kind === "reply" || Kind === "edit") && Text.trim().length === 0) { Why.push("Type something first."); return null; }
            if (Text.length > AppOutbox.MaxText) { Why.push("That is " + String(Text.length) + " characters. The limit is " + String(AppOutbox.MaxText) + "."); return null; }
            if (Who.length === 0) { Why.push("Not signed in - nothing can approve a send."); return null; }
            let Words: string = Text;
            if (Kind === "revoke" || Kind === "forward") { Words = ""; }
            const Out: AppSendRequest = AppOutbox.Made(ChatId, Title, Words, Who);
            Out.ActKind = Kind;
            Out.ActOn = MessageId;
            Out.ActFrom = From;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOutbox.BuildAction");
            return null;
        }
    };

    private static Made = (ChatId: string, Title: string, Text: string,
                           Who: string): AppSendRequest => {
        if (getCoreUtils().KErrorMode === true) { return new AppSendRequest(); }
        try {
            const Out: AppSendRequest = new AppSendRequest();
            Out.Id = AppOutbox.NewId();
            Out.ChatId = ChatId;
            Out.ChatTitle = Title;
            Out.Text = Text;
            Out.RequestedIso = new Date().toISOString();
            Out.ApprovedBy = Who;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOutbox.Made");
            return new AppSendRequest();
        }
    };

    /**
     * ⚠ THE ID IS THE IDEMPOTENCY KEY, AND IT IS GENERATED ONCE PER PRESS. The bridge's ledger
     * refuses to send the same id twice, so a file that syncs twice, a folder restored from a
     * backup, or a second bridge reading the same Drive all cost nothing. Generating it later -
     * say, when the upload is retried - would hand the same message two identities.
     *
     * It carries the time in the NAME as well as the body so the owner can read the folder without
     * opening anything.
     */
    public static NewId = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Stamp: string = new Date().toISOString().replace(/[^0-9]/g, "").substring(0, 14);
            return "s-" + Stamp + "-" + kCore.RandomHex(4);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOutbox.NewId");
            return "";
        }
    };

    /**
     * Mirrors `Outbox.IsJid`. See that file for why `@lid` is admitted and is the one to watch.
     *
     * DOCUMENTED EXCEPTION: pure predicate over a string already in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: false IS the answer for anything that is not one.
     */
    public static IsJid = (Value: string): boolean => {
        return WhatsAppAddress.IsJid(Value);
    };

    /** The file the bridge will read. Named after the id, so a receipt lands beside it. */
    public static FileName = (Req: AppSendRequest): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return Req.Id + ".json";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOutbox.FileName");
            return "";
        }
    };

    public static Json = (Req: AppSendRequest): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: Record<string, unknown> = {};
            Out.schema = APP_OUTBOX_SCHEMA;
            if (Req.Attachment !== null) { Out.schema = AppOutbox.AttachmentSchema; Out.attachment = Req.Attachment; }
            Out.id = Req.Id;
            Out.chatId = Req.ChatId;
            Out.chatTitle = Req.ChatTitle;
            Out.text = Req.Text;
            Out.requestedIso = Req.RequestedIso;
            Out.approvedBy = Req.ApprovedBy;
            // ⛔ The message id and the emoji - `SendRules.AskIn` reads exactly these two and nothing else.
            if (Req.IsReaction === true) { Out.react = { messageId: Req.ReactTo, emoji: Req.Emoji }; }
            // ⛔ The kind and the message id - `SendRules.ActAskIn` reads exactly these two.
            if (Req.ActKind.length > 0) { Out.act = { kind: Req.ActKind, messageId: Req.ActOn }; }
            if (Req.ActKind === "forward") { (Out.act as Record<string, unknown>).from = Req.ActFrom; }
            // ⭐ THE ONLY FLAG THE PAGE SENDS FOR TRANSLATION - the service decides the words that go out.
            if (Req.Translate === true && Req.IsReaction === false) { Out[AppTranslate.SendField] = true; }
            return JSON.stringify(Out, null, 1);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOutbox.Json");
            return "";
        }
    };
}
